import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import LocationFilter from 'components/Shared/LocationFilter/LocationFilter';
import userShape from 'propTypes/userShape';
import JobStatusOpenFilter from './JobStatusOpenFilter/JobStatusOpenFilter';
import ExperienceLevelFilter from './ExperienceLevelFilter/ExperienceLevelFilter';
import HiringCapacityFilter from './HiringCapacityFilter/HiringCapacityFilter';
import SvgIcon from 'components/SvgIcon/SvgIcon';

import styles from './BriefFilter.module.scss';
import RoleFilterResetButton from './RoleFilterResetButton/RoleFilterResetButton';
import { Col, Collapse, Grid, Row, Button, FormControl } from 'react-bootstrap';
import RolesSelector from 'components/Shared/Selectors/RolesSelector';
import { fromSeoUrl } from 'helpers/HelperFunctions';

const BriefFilter = ({
  user,
  paid,

  filter,
  onFiltersReset,
  onStatusFilterToggle,
  onRemoteFilterToggle,
  onInternalFilterToggle,

  onExperienceLevelToggle,
  onHiringCapacityToggle,

  location,

  onLocationTextChange,

  onSearchTermChange,

  filterList,
  onEditButton,
  onRolesSelect,
  onRolesHaveChanged,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  //this is to force a re-render so the role selector shows prefilled options
  useEffect(() => {
    if (open) {
      setLoading(false);
    }

    if (filter.searchTerm === null) {
      setSearchTerm('');
    } else {
      setSearchTerm(filter.searchTerm);
    }
  }, [open, filter]);

  const currentLocation =
    filter.locationText && fromSeoUrl(filter.locationText);

  const funcToUse = user ? () => setOpen(!open) : onEditButton;

  return (
    <Grid
      id="filterCollapse"
      className={[
        styles.briefFilterContainer,
        open && styles.filterActive,
        filterList && !open && styles.closedFilterContainer,
      ]}
    >
      <Row>
        <Col md={12} className={styles.filterColumn}>
          <div className={styles.titleContainer}>
            <p className={styles.title}>
              <SvgIcon icon="filter_list" style={{ marginRight: '10px' }} />
              <strong>
                Filter:<span className="ml--">{filterList}</span>
              </strong>
            </p>
            {!open && (
              <div className={styles.titleButtonContainer}>
                <Button
                  bsStyle="link"
                  className={styles.filterEditButton}
                  onClick={funcToUse}
                  aria-controls="filterContainer"
                  aria-expanded={open}
                >
                  Edit
                </Button>
                {user && (
                  <RoleFilterResetButton
                    paid={paid}
                    filter={filter}
                    filterList={filterList}
                    onFiltersReset={onFiltersReset}
                    location={location}
                  />
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Collapse in={open}>
        <div id="filterContainer">
          <Row>
            <Col md={12} className={styles.filterColumn}>
              <p className={`${styles.filterTitle} ${styles.searchTitle}`}>
                Search
              </p>
              <FormControl
                type="text"
                value={searchTerm}
                placeholder="Enter any keywords that match the jobs your looking for"
                onChange={(event) => setSearchTerm(event.target.value)}
                onBlur={() => onSearchTermChange(searchTerm)}
                maxLength="140"
                style={{ fontSize: '15px' }}
              />
            </Col>
          </Row>
          <Row>
            {!loading && (
              <Col md={6} className={styles.filterColumn}>
                <p className={styles.filterTitle}>Roles</p>
                <RolesSelector
                  key={JSON.stringify(filter.selectedRoles)}
                  className={styles.simpleSelect}
                  existing={filter.selectedRoles}
                  onSelect={(roles) => {
                    onRolesSelect(roles);
                    onRolesHaveChanged();
                  }}
                />
              </Col>
            )}

            <Col md={6} className={styles.filterColumn}>
              <p className={styles.filterTitle}>Job Location</p>
              <LocationFilter
                currentLocation={currentLocation}
                onLocationChange={onLocationTextChange}
                googleAPIType="regions"
                locationInputId="jobLocationFilter"
                shouldClearOnBlur={!filter.locationText}
                placeHolder="Enter Location"
              />
            </Col>
          </Row>
          <Row>
            <Col
              md={6}
              className={[styles.filterColumn, styles.checkboxFiltersContainer]}
            >
              <div className={styles.checkboxFilter}>
                <p className={styles.filterTitle}>Experience</p>
                <ExperienceLevelFilter
                  experienceLevels={filter.experienceLevels}
                  onExperienceLevelToggle={onExperienceLevelToggle}
                />
              </div>
              <div className={styles.checkboxFilter}>
                <p className={styles.filterTitle}>Type</p>
                <HiringCapacityFilter
                  hiringCapacities={filter.hiringCapacities}
                  onHiringCapacityToggle={onHiringCapacityToggle}
                />
              </div>
            </Col>
            <Col
              md={6}
              className={[styles.filterColumn, styles.toggleFiltersContainer]}
            >
              <div className={styles.toggleFilter}>
                <p className={styles.filterTitle}>Remote Jobs</p>
                <JobStatusOpenFilter
                  isActive={filter.remote}
                  onFilterToggle={onRemoteFilterToggle}
                />
              </div>
              <p className={styles.infoText}>
                Jobs with no specified location, open to the global network
              </p>

              <div className={styles.toggleFilter}>
                <p className={styles.filterTitle}>Open Jobs</p>
                <JobStatusOpenFilter
                  isActive={filter.status}
                  onFilterToggle={onStatusFilterToggle}
                />
              </div>
              <p className={styles.infoText}>
                Jobs currently open to applications
              </p>

              <div className={styles.toggleFilter}>
                <p className={styles.filterTitle}>Easy Apply ⚡</p>
                <JobStatusOpenFilter
                  isActive={filter.internal}
                  onFilterToggle={onInternalFilterToggle}
                />
              </div>
              <p className={styles.infoText}>
                Jobs posted directly onto the Twine platform by our clients
              </p>
            </Col>
          </Row>
          <Row>
            <Col
              className={[styles.filterColumn, styles.filterButtonContainer]}
            >
              {user && (
                <RoleFilterResetButton
                  paid={paid}
                  filter={filter}
                  filterList={filterList}
                  onFiltersReset={onFiltersReset}
                  location={location}
                />
              )}
              <Button
                bsStyle="primary"
                className={styles.filterCloseButton}
                onClick={() => {
                  setOpen(!open);
                  onSearchTermChange(searchTerm);
                }}
                aria-controls="filterContainer"
                aria-expanded={open}
              >
                Search
              </Button>
            </Col>
          </Row>
        </div>
      </Collapse>
    </Grid>
  );
};

BriefFilter.propTypes = {
  user: userShape,
  paid: PropTypes.bool,

  filter: PropTypes.object,
  onFiltersReset: PropTypes.func,
  onStatusFilterToggle: PropTypes.func,
  onRemoteFilterToggle: PropTypes.func,
  onInternalFilterToggle: PropTypes.func,

  roleSkills: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  loadingRoleSkills: PropTypes.bool,
  onSelectedRoleChange: PropTypes.func,

  experienceLevels: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onExperienceLevelToggle: PropTypes.func,

  hiringCapacities: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onHiringCapacityToggle: PropTypes.func,

  location: PropTypes.object,

  locationTextFilter: PropTypes.string,
  onLocationTextChange: PropTypes.func,

  onSearchTermChange: PropTypes.func,

  selectedRole: PropTypes.object,
  suggestedRoles: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  loadingRoles: PropTypes.bool,

  filterList: PropTypes.string,

  onEditButton: PropTypes.func,

  onRolesSelect: PropTypes.func,
  onRolesHaveChanged: PropTypes.func,
};

BriefFilter.defaultProps = {
  user: null,
  paid: null,
  loadingRoleSkills: false,

  roleSkills: [],
  selectedRole: {},
  experienceLevels: [],
  hiringCapacities: [],

  filterList: '',
};

export default BriefFilter;
